/**
 * A table on a desk, stacked rows on a phone, decided by the table's own width (a page, a pane or a phone), never by
 * a column's name. Each column says how it folds with `narrow`:
 * - `main`: the row's title, top left.  - `end`: top right (a status chip).
 * - `below`: under the title, labelled.  - `hide`: gone when narrow.
 * `drop: 'medium'` also hides a column at middle widths (meters on a tablet). `empty` is required: say why.
 * With `onOpen`, rows open on click or Enter, and `j` / `k` move between them.
 */
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'
import { cx } from '../cx'
import { Icon } from '../primitives/Icon'
import s from './DataTable.module.css'

export interface Column<T> {
  key: string
  label: string
  render: (row: T) => ReactNode
  narrow?: 'main' | 'end' | 'below' | 'hide'
  /** Below-cells show their label when narrow; turn off when the content says it (lights with names). */
  narrowLabel?: boolean
  drop?: 'medium'
  align?: 'start' | 'end'
}

export function DataTable<T>({
  label,
  rows,
  rowKey,
  columns,
  empty,
  onOpen,
  selected,
  attention,
  framed = true,
}: {
  /** What the table lists, for screen readers ("Boxes"). */
  label: string
  rows: T[]
  rowKey: (row: T) => string
  columns: Column<T>[]
  /** Shown instead of the table when there are no rows. Required: an empty list says why. */
  empty: ReactNode
  onOpen?: (row: T) => void
  /** The key of the row whose detail is open. */
  selected?: string | null
  /** A Signal bar on the row's edge: down, or High and not acknowledged. */
  attention?: (row: T) => boolean
  /** Draw its own sheet. Off inside a Card. */
  framed?: boolean
}) {
  if (!rows.length) return <div className={cx(s.box, framed && s.framed)}>{empty}</div>

  const narrowOf = (c: Column<T>, i: number) => c.narrow ?? (i === 0 ? 'main' : 'below')

  const onKeys = (e: KeyboardEvent<HTMLTableSectionElement>) => {
    if (!onOpen || !['j', 'k', 'ArrowDown', 'ArrowUp'].includes(e.key)) return
    const trs = Array.from(e.currentTarget.querySelectorAll<HTMLTableRowElement>('tr[data-row]'))
    const at = trs.indexOf(document.activeElement as HTMLTableRowElement)
    const next = e.key === 'j' || e.key === 'ArrowDown' ? at + 1 : at < 0 ? trs.length - 1 : at - 1
    const tr = trs[Math.max(0, Math.min(trs.length - 1, next))]
    if (tr) e.preventDefault(), tr.focus()
  }
  // A click on a button or link inside a row is that control's, not the row's.
  const fromControl = (e: MouseEvent | KeyboardEvent) => (e.target as HTMLElement).closest('button, a, input, select, textarea, label') !== null

  return (
    <div className={cx(s.box, framed && s.framed)}>
      <table className={s.table} aria-label={label}>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={c.key} scope="col" className={cx(s[`n-${narrowOf(c, i)}`], c.drop && s.dropMedium, c.align === 'end' && s.end)}>
                {c.label}
              </th>
            ))}
            {onOpen && <th className={s['n-hide']} aria-label="Open" />}
          </tr>
        </thead>
        <tbody onKeyDown={onKeys}>
          {rows.map((r) => {
            const k = rowKey(r)
            return (
              <tr
                key={k}
                data-row
                className={cx(onOpen && s.openable, attention?.(r) && s.attn, selected === k && s.sel)}
                tabIndex={onOpen ? 0 : undefined}
                aria-selected={onOpen ? selected === k : undefined}
                onClick={onOpen ? (e) => !fromControl(e) && onOpen(r) : undefined}
                onKeyDown={onOpen ? (e) => e.key === 'Enter' && !fromControl(e) && (e.preventDefault(), onOpen(r)) : undefined}
              >
                {columns.map((c, i) => {
                  const n = narrowOf(c, i)
                  return (
                    <td key={c.key} className={cx(s[`n-${n}`], c.drop && s.dropMedium, c.align === 'end' && s.end, n === 'below' && c.narrowLabel !== false && s.labelled)} data-label={c.label}>
                      {c.render(r)}
                    </td>
                  )
                })}
                {onOpen && (
                  <td className={cx(s['n-hide'], s.chev)}>
                    <Icon name="chev" size="sm" />
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
